/* global MutationObserver, WheelEvent, document, window */

import * as React from 'react';
import {
  withHandlers,
  withState,
  flattenProp,
  lifecycle,
  compose,
} from 'react-recompose';
import {defaults, mapValues, clamp, debounce as lodashDebounce} from 'lodash';
import {relativeCoordinates} from './logic/domEvents';
import {syncPropsToState} from '@packages/utilities/component';
import {styledDiv} from '@packages/utilities/styled';
import {assoc, emptyObject} from '@packages/utilities/fp/utils';
import {list, shadow, translate} from '@packages/utilities/inline-style-utils';
import {fill} from './constants/Styles';
import Scrollbar from './Scrollbar';

/**
 * This file is currently blocking the Flow to Typescript migration.
 *
 * The flow types currently in this file are largely incomplete or incorrect.
 *
 * Based on the fact that 1) these types were never correct and 2) we will be deprecating this component,
 * DS has decided to remove the typing entirely in favor of unblocking the TS migration.
 *
 * We should reassess this judgment if Spring's ScrollView is delayed significantly or de-prioritized entirely
 *
 * @see https://webflow.atlassian.net/browse/PDTY-3889
 */

const debounce = (funcName, delay) => (Component) => {
  const factory = (factoryProps) =>
    React.createElement(Component, factoryProps);

  class Debounce extends React.Component {
    fn = lodashDebounce((...args) => {
      this.props[funcName](...args);
    }, delay);

    componentWillUnmount() {
      this.fn.cancel();
    }

    render() {
      return factory(
        defaults(
          {
            [funcName]: this.fn,
          },
          this.props
        )
      );
    }
  }

  return Debounce;
};

const AUTOSCROLL_THRESHOLD = 20;

const LEFT_MOUSE_BUTTON = 0;

const horizontalScrollbarStyle = {
  position: 'absolute',
  bottom: 0,
  left: 0,
};

const verticalScrollbarStyle = {
  position: 'absolute',
  top: 0,
  right: 0,
};

// NOTICE: `width` and `height` props are deprecated. Use `containerWidth`, and `containerHeight` props instead. `width` and `height` should eventually be removed with `ScrollViewWithState` instances are refactored to use the newer props.

const provideHandlers = withHandlers({
  onWheel:
    ({
      width,
      height,
      containerWidth = width,
      containerHeight = height,
      scrollWidth,
      scrollHeight,
      scrollTop = 0,
      scrollLeft = 0,
      onScroll,
    }) =>
    (event) => {
      const nextScrollTop =
        scrollHeight > containerHeight && event.deltaY
          ? clamp(scrollTop + event.deltaY, 0, scrollHeight - containerHeight)
          : scrollTop;

      const nextScrollLeft =
        scrollWidth > containerWidth && event.deltaX
          ? clamp(scrollLeft + event.deltaX, 0, scrollWidth - containerWidth)
          : scrollLeft;

      if (event) {
        // Need to check if the event is cancellable before preventing. This stops a constant error from triggering in the console.
        event.cancelable && event.preventDefault();
        event.stopPropagation();
      }

      if (nextScrollTop !== scrollTop || nextScrollLeft !== scrollLeft) {
        onScroll({
          scrollTop: nextScrollTop,
          scrollLeft: nextScrollLeft,
        });
      }
    },
  onVerticalScroll:
    ({onScroll, scrollLeft}) =>
    (scrollTop) =>
      onScroll({scrollTop, scrollLeft}),
  onHorizontalScroll:
    ({onScroll, scrollTop}) =>
    (scrollLeft) =>
      onScroll({scrollTop, scrollLeft}),
});

const autoscroll = lifecycle({
  componentDidUpdate() {
    if (this.props.autoscrolling) {
      const ownerWindow = window;

      ownerWindow.cancelAnimationFrame(this.frameId);
      this.frameId = ownerWindow.requestAnimationFrame(() => {
        const {onWheel, velocityX, velocityY, autoscrolling} = this.props;

        if (autoscrolling) {
          onWheel(
            new WheelEvent('wheel', {
              deltaX: velocityX / 2,
              deltaY: velocityY / 2,
            })
          );
        }
      });
    }
  },
});

const directions = ['Top', 'Bottom', 'Left', 'Right'];

const xMultipliers = {
  Top: 0,
  Bottom: 0,
  Left: 1,
  Right: -1,
};

const yMultipliers = {
  Top: 1,
  Bottom: -1,
  Left: 0,
  Right: 0,
};

const Shadow = styledDiv((props) => {
  const {shadowColor, shadowSize} = props.theme.scrollView;

  return defaults(
    {
      boxShadow: list(
        ...directions.reduce((shadows, direction) => {
          const scrollAmount = props['scroll' + direction];

          if (scrollAmount) {
            const clampedScrollAmount = clamp(scrollAmount, 0, shadowSize);

            shadows.push(
              shadow(
                'inset',
                xMultipliers[direction] * clampedScrollAmount,
                yMultipliers[direction] * clampedScrollAmount,
                shadowSize,
                -shadowSize,
                shadowColor
              )
            );
          }

          return shadows;
        }, [])
      ),
      pointerEvents: 'none',
    },
    fill
  );
});

export const ScrollView = provideHandlers(
  autoscroll(
    ({
      width,
      height,
      style = {},
      containerWidth = width,
      containerHeight = height,
      scrollHeight = height,
      scrollWidth = width,
      setOuterContainer,
      scrollTop = 0,
      scrollLeft = 0,
      scrollingVertically,
      scrollingHorizontally,
      showShadows = true,
      onVerticalScroll,
      onHorizontalScroll,
      onWheel,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      children,
      scrolling,
      largeScrollbar,
    }) => {
      return (
        // TODO: fix jsx-a11y lint error
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          ref={setOuterContainer}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onDragStart={onMouseDown}
          onMouseMove={onMouseMove}
          onDragOver={onMouseMove}
          onMouseUp={onMouseUp}
          style={{
            position: 'relative',
            overflow: 'hidden',
            width,
            height,
            ...style,
          }}
          data-legacy-ds="ScrollView"
        >
          {children}
          {showShadows ? (
            <Shadow
              scrollTop={scrollTop}
              scrollBottom={scrollHeight - containerHeight - scrollTop}
              scrollLeft={scrollLeft}
              scrollRight={scrollWidth - containerWidth - scrollLeft}
            />
          ) : null}
          <Scrollbar
            horizontal
            style={horizontalScrollbarStyle}
            scrollPosition={scrollLeft}
            scrollLength={scrollWidth}
            viewLength={containerWidth}
            scrolling={scrollingHorizontally || scrolling}
            onScroll={onHorizontalScroll}
            large={largeScrollbar}
          />
          <Scrollbar
            style={verticalScrollbarStyle}
            scrollPosition={scrollTop}
            scrollLength={scrollHeight}
            viewLength={containerHeight}
            scrolling={scrollingVertically || scrolling}
            onScroll={onVerticalScroll}
            large={largeScrollbar}
          />
        </div>
      );
    }
  )
);

ScrollView.displayName = 'ScrollView';

const setPointerIsDown = assoc('pointerIsDown')(true);
const setPointerUpState = (state) => ({
  scrollingVertically: state.scrollingVertically,
  scrollingHorizontally: state.scrollingHorizontally,
  pointerIsDown: false,
  autoscrolling: false,
  velocityX: state.velocityX,
  velocityY: state.velocityY,
});

const initialState = {
  scrollingVertically: false,
  scrollingHorizontally: false,
  pointerIsDown: false,
  autoscrolling: false,
  velocityX: 0,
  velocityY: 0,
};

const scrollViewState = compose(
  withState('state', 'setState', initialState),
  flattenProp('state'),
  withHandlers({
    stopScrolling:
      ({setState, pointerIsDown}) =>
      () =>
        !pointerIsDown && setState(initialState),
  }),
  debounce('stopScrolling', 500),
  withHandlers({
    onScroll: (props) => (values) => {
      props.setState({
        scrollingVertically:
          values.scrollTop !== props.scrollTop || props.scrollingVertically,
        scrollingHorizontally:
          values.scrollLeft !== props.scrollLeft || props.scrollingHorizontally,
        pointerIsDown: props.pointerIsDown,
        autoscrolling: props.autoscrolling,
        velocityX: props.velocityX,
        velocityY: props.velocityY,
      });
      props.onScroll(values);
      props.stopScrolling();
    },
    onMouseDown:
      ({setState}) =>
      (event) => {
        if (event.button === LEFT_MOUSE_BUTTON) {
          setState(setPointerIsDown);
        }
      },
    onMouseMove:
      ({
        scrollingVertically,
        scrollingHorizontally,
        pointerIsDown,
        velocityX,
        velocityY,
        width,
        height,
        containerWidth = width,
        containerHeight = height,
        setState,
        stopScrolling,
      }) =>
      (event) => {
        // Keep showing scrollbars while the mouse is moving in case the user
        // wants to get to them. Because `stopScrolling` is debounced this call
        // stops the scrolling only after the last time it is called.
        stopScrolling();

        if (!pointerIsDown) {
          return;
        }

        const {x, y} = relativeCoordinates(event);

        const newVelocityX =
          x < AUTOSCROLL_THRESHOLD
            ? x - AUTOSCROLL_THRESHOLD
            : x > containerWidth - AUTOSCROLL_THRESHOLD
              ? x + AUTOSCROLL_THRESHOLD - containerWidth
              : 0;

        const newVelocityY =
          y < AUTOSCROLL_THRESHOLD
            ? y - AUTOSCROLL_THRESHOLD
            : y > containerHeight - AUTOSCROLL_THRESHOLD
              ? y + AUTOSCROLL_THRESHOLD - containerHeight
              : 0;

        const newAutoscrolling = newVelocityX !== 0 || newVelocityY !== 0;

        if (velocityX !== newVelocityX || velocityY !== newVelocityY) {
          setState({
            scrollingVertically,
            scrollingHorizontally,
            pointerIsDown,
            autoscrolling: newAutoscrolling,
            velocityX: newVelocityX,
            velocityY: newVelocityY,
          });
        }
      },
    onMouseUp:
      ({setState, stopScrolling}) =>
      () => {
        setState(setPointerUpState);
        stopScrolling();
      },
  })
);

// Optimization. Round since getBoundingClientRect doesn't always return the same dimensions -- even when an element doesn't change. (CC)
const roundBox = ({left, right, top, bottom, width, height}) =>
  mapValues({left, right, top, bottom, width, height}, Math.round);

const boxEquals = (a, b) =>
  a.left === b.left &&
  a.right === b.right &&
  a.top === b.top &&
  a.bottom === b.bottom;

const isBox = (rect) => {
  const BOX_KEYS = ['left', 'right', 'top', 'bottom', 'width', 'height'];
  return BOX_KEYS.every((key) => rect.hasOwnProperty(key));
};

const ScrollViewScrollState = scrollViewState(ScrollView);

const withScrollViewState = syncPropsToState({
  scrollPosition: {
    updaterName: 'setScrollPosition',
    initialValue: () => ({scrollLeft: 0, scrollTop: 0}),
  },
});

const ScrollViewWithState = withScrollViewState(
  ({
    setScrollPosition,
    scrollPosition = {scrollLeft: 0, scrollTop: 0},
    style,
    scrollRect = {},
    onScroll,
    scrollLeft,
    scrollTop,
    scrollWidth,
    scrollHeight,
    containerRect = {},
    children,
    width,
    height,
    isAnimated,
    onScrollDone,
    ...rest
  }) => {
    const containerWidth = width || containerRect.width;
    const containerHeight = height || containerRect.height;
    scrollWidth = scrollWidth || scrollRect.width;
    scrollHeight = scrollHeight || scrollRect.height;

    scrollTop = clamp(
      scrollTop || scrollPosition.scrollTop,
      0,
      scrollHeight - containerHeight
    );
    scrollLeft = clamp(
      scrollLeft || scrollPosition.scrollLeft,
      0,
      scrollWidth - containerWidth
    );
    return (
      <ScrollViewScrollState
        {...rest}
        style={style}
        width={width}
        height={height}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        scrollTop={scrollTop}
        scrollLeft={scrollLeft}
        scrollHeight={scrollHeight}
        scrollWidth={scrollWidth}
        onScroll={onScroll || setScrollPosition}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            transform: translate(
              -Math.round(scrollLeft),
              -Math.round(scrollTop)
            ),
            ...(isAnimated ? {transition: `transform 1s ease-in-out 0s`} : {}),
          }}
          onTransitionEnd={onScrollDone}
        >
          {children}
        </div>
      </ScrollViewScrollState>
    );
  }
);

export default ScrollViewScrollState;
export {ScrollViewWithState};

class Scrolly extends React.PureComponent {
  state = {
    scrollLeft: 0,
    scrollTop: 0,
    scrollRect: this.props.scrollRect || emptyObject,
    containerRect: this.props.containerRect || emptyObject,
    isAnimated: false,
    onScrollDone: null,
    elementInTheView: null,
  };

  innerContainer = null;
  outerContainer = null;

  setInnerContainer = (ref) => {
    if (ref) {
      this.innerContainer = ref;
    }
  };

  setOuterContainer = (ref) => {
    if (ref) {
      this.outerContainer = ref;
    }
  };

  onScroll = ({scrollLeft, scrollTop}) => {
    this.setState({
      scrollLeft,
      scrollTop,
      isAnimated: false,
      onScrollDone: null,
    });
  };

  componentDidMount() {
    this._activeDocumentElement = document.activeElement;

    const ownerDocument = this.outerContainer.ownerDocument;

    // Listen for whenever ANY DOM node changes since mutations may affect the height
    // of this container (siblings, and children) - especially since other elements may contain layout props such
    // as height: 100vh, 100%, padding, margins, and other CSS props that we cannot inspect will affect the container & scroll heights. Child components may control their
    // own internal state, and sibling elements may also affect the ScrollView layout.
    //
    // A good solution (albiet not perfect since doesn't listen for CSS repaints -- it's decent since we explicitly define element styles in most places)
    // is to listen for attribute & child changes in the entire document, then fire a `updateScrollSize` and check whether the
    // container or scroll height has changed. This should provide a better Just Works EnhancedScrollView components with few side effects.
    //
    // Technically you can use the ScrollView component in a more pure way, but that can only be the case if we explicitly define _every_ width & height property in the application since
    // siblings & children both affect the container & scroll height of a component. Since this will never happen, the somewhat in-between solution is to use synthetic event listeners in conjuction with `getBoundingClientRect`.
    //
    // We can explicitly define scroll & container size information for the ScrollView by cheating a bit with `getBoundingClientRect`, but this also has a number of issues:
    // - ScrollView children must notify the ScrollView component of any DOM changes, even children that maintain internal state.
    // - ScrollView component must be aware sibling element resizes.
    // - ScrollView must be aware of browser resizes.
    //
    // All of this requires for manually written scroll size changing code that isn't very maintainable, or scalable.
    //
    // There are some architectual decisions that we can make to make this component more pure, but we haven't gotten there yet as of this comment.
    // This component will _always_ be leaky since we cannot listen for browser repaints - there's risk that CSS mutations won't
    // trigger a ScrollView resize -- it can't be avoided. The best option we have is to a) use native scrolling, or b) use a solution that listens for native events
    // that might cause CSS repaints. Any (CC)

    this._mutationObserver = new MutationObserver(this.onChildrenChanged);
    this._mutationObserver.observe(ownerDocument, {
      attributes: true,
      subtree: true,
      childList: true,
      characteData: true,
    });

    // Resizing the browser will also affect the CSS layout. I.E: the container & scroll size might change.
    ownerDocument.defaultView.addEventListener('resize', this.updateScrollSize);

    this.updateScrollSize();
    // TODO - listen for "focus" event and scroll to element position
  }

  componentWillUnmount() {
    this.outerContainer.ownerDocument.defaultView.removeEventListener(
      'resize',
      this.updateScrollSize
    );
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
    }

    this.innerContainer = null;
    this.outerContainer = null;
  }

  onChildrenChanged = () => {
    this.updateScrollSize();

    // scroll to elements that have the data-active flag which is set when they're being displayed
    // in an active (or highlighted) state. See TimelineActionList for an example of this.
    const activeElements = Array.prototype.slice.call(
      this.outerContainer.querySelectorAll('[data-active=true]')
    );
    if (
      activeElements.length &&
      this._activeDataElement !== activeElements[0]
    ) {
      this.scrollToElement((this._activeDataElement = activeElements[0]));
    }
  };

  scrollToElement = (element, onScrollDone) => {
    const {scrollTop, containerRect, scrollLeft} = this.state;
    const {
      containerOffset,
      animateScrollToElement = false,
      scrollToRelativeTop = false,
    } = this.props;
    const {
      top: containerOffsetTop = 0,
      bottom: containerOffsetBottom = 0,
      left: containerOffsetLeft = 0,
      right: containerOffsetRight = 0,
    } = containerOffset || {};
    let scrolled = false;

    if (this.outerContainer && this.outerContainer.contains(element)) {
      const activeRect = element.getBoundingClientRect();
      const relativeLeft = activeRect.left - this._scrollRect.left;
      const relativeTop = activeRect.top - this._scrollRect.top;
      const newScrollLeft =
        scrollLeft > relativeLeft
          ? relativeLeft - containerOffsetLeft
          : containerRect.width + scrollLeft < relativeLeft + activeRect.width
            ? relativeLeft -
              containerRect.width +
              activeRect.width +
              containerOffsetRight
            : scrollLeft;

      const newScrollTop = scrollToRelativeTop
        ? relativeTop
        : scrollTop > relativeTop
          ? relativeTop - containerOffsetTop
          : containerRect.height + scrollTop < relativeTop + activeRect.height
            ? relativeTop -
              containerRect.height +
              activeRect.height +
              containerOffsetBottom
            : scrollTop;

      if (newScrollLeft !== scrollLeft || newScrollTop !== scrollTop) {
        this.setState({
          scrollLeft: newScrollLeft,
          scrollTop: newScrollTop,
          isAnimated: animateScrollToElement,
          onScrollDone,
        });
        scrolled = true;
      }
    }

    if (!scrolled) {
      onScrollDone && onScrollDone();
    }
  };

  scrollToElementOnce = (element) => {
    if (element && this.state.elementInTheView !== element) {
      this.setState({elementInTheView: element});
      this.scrollToElement(element);
    }
  };

  resetScroll = () => {
    this.onScroll({scrollLeft: 0, scrollTop: 0});
  };

  updateScrollSize = () => {
    const {onContainerRectChange, centeredHorizontally} = this.props;

    const {containerRect, scrollRect} = this.state;

    if (!this.innerContainer) {
      return;
    }

    const innerContainerRect = this.innerContainer.getBoundingClientRect();
    const newScrollRect = (this._scrollRect = roundBox({
      left: innerContainerRect.left,
      right: innerContainerRect.right,
      top: innerContainerRect.top,
      bottom: innerContainerRect.bottom,
      height: innerContainerRect.height,
      width: Math.max(
        innerContainerRect.width,
        this.innerContainer.scrollWidth
      ),
    }));

    const newContainerRect = (this._containerRect = roundBox(
      this.outerContainer.getBoundingClientRect()
    ));
    const scrollRectChanged = !boxEquals(scrollRect, newScrollRect);
    const containerRectChanged = !boxEquals(containerRect, newContainerRect);

    const newState = {};

    if (scrollRectChanged) {
      newState.scrollRect = newScrollRect;
    }

    if (containerRectChanged) {
      newState.containerRect = newContainerRect;
    }

    if (centeredHorizontally) {
      // if it is the first render of the component state changes here will make sure that the element is shown centered horizontally
      const isFirstScrollRectSetting =
        !isBox(scrollRect) &&
        !isBox(containerRect) &&
        isBox(newScrollRect) &&
        isBox(newContainerRect);

      if (isFirstScrollRectSetting) {
        newState.scrollLeft = Math.round(
          (newScrollRect.width - newContainerRect.width) / 2
        );
      }
    }

    if (Object.keys(newState).length) {
      this.setState(
        newState,
        onContainerRectChange
          ? () => {
              onContainerRectChange(newContainerRect);
            }
          : undefined
      );
    }

    // native scroll position may be set if a child element is focused out of view --
    // reset scroll position here if that's the case.
    this.outerContainer.scrollLeft = this.outerContainer.scrollTop = 0;

    if (this._activeDocumentElement !== document.activeElement) {
      this._activeDocumentElement = document.activeElement;
      if (this.outerContainer.contains(this._activeDocumentElement)) {
        this.scrollToElement(this._activeDocumentElement);
      }
    }
  };

  handlers = {
    setInnerContainer: this.setInnerContainer,
    setOuterContainer: this.setOuterContainer,
    scrollToElement: this.scrollToElement,
    scrollToElementOnce: this.scrollToElementOnce,
    resetScroll: this.resetScroll,
    onScroll: this.onScroll,
  };

  render() {
    return this.props.children(this.state, this.handlers);
  }
}

export const EnhancedScrollView = ({
  children,
  handleScrollyRef,
  horizontalScroll,
  containerOffset,
  animateScrollToElement,
  centeredHorizontally,
  scrollToRelativeTop,
  ...props
}) => {
  const ariaLabel = props['aria-label'];
  const dataAutomationId = props['data-automation-id'];
  const Tag = horizontalScroll ? 'div' : 'span';
  return (
    <Scrolly
      ref={handleScrollyRef}
      containerOffset={containerOffset}
      animateScrollToElement={animateScrollToElement}
      centeredHorizontally={centeredHorizontally}
      scrollToRelativeTop={scrollToRelativeTop}
    >
      {(state, handlers) => (
        <ScrollViewWithState
          onScroll={handlers.onScroll}
          setOuterContainer={handlers.setOuterContainer}
          {...state}
          {...props}
        >
          <Tag
            aria-label={ariaLabel}
            data-automation-id={dataAutomationId}
            ref={handlers.setInnerContainer}
          >
            {typeof children === 'function'
              ? children(state, handlers)
              : children}
          </Tag>
        </ScrollViewWithState>
      )}
    </Scrolly>
  );
};
